<script setup>
console.log('🚀 GM Metric Card: Initializing metric card component...');

import { computed } from 'vue';

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  value: {
    type: [String, Number],
    required: true
  },
  description: {
    type: String,
    default: ''
  },
  color: {
    type: String,
    default: '#3b82f6'
  },
  icon: {
    type: String,
    default: 'icon-metric'
  },
  trend: {
    type: Object,
    default: null
  }
});

console.log('📊 GM Metric Card: Props received:', {
  title: props.title,
  value: props.value,
  description: props.description,
  color: props.color,
  icon: props.icon
});

// Computed properties
const hasTrend = computed(() => {
  const hasTrendData = props.trend && (props.trend.value !== undefined || props.trend.percentage !== undefined);
  console.log('📈 GM Metric Card: Has trend data:', hasTrendData);
  return hasTrendData;
});

const trendIcon = computed(() => {
  if (!hasTrend.value) return '';
  
  const trendValue = props.trend.value || props.trend.percentage || 0;
  const isPositive = trendValue > 0;
  const isNeutral = trendValue === 0;
  
  console.log('📈 GM Metric Card: Trend value:', trendValue, 'Is positive:', isPositive);
  
  if (isNeutral) return 'icon-trend-neutral';
  return isPositive ? 'icon-trend-up' : 'icon-trend-down';
});

const trendColor = computed(() => {
  if (!hasTrend.value) return props.color;
  
  const trendValue = props.trend.value || props.trend.percentage || 0;
  const isPositive = trendValue > 0;
  const isNeutral = trendValue === 0;
  
  if (isNeutral) return '#64748b';
  return isPositive ? '#10b981' : '#ef4444';
});

const formattedValue = computed(() => {
  if (typeof props.value === 'number') {
    return props.value.toLocaleString();
  }
  return props.value;
});

console.log('✅ GM Metric Card: Component setup completed');
</script>

<template>
  <div class="gm-metric-card" :style="{ '--metric-color': color }">
    <div class="gm-metric-header">
      <div class="gm-metric-icon">
        {{ icon }}
      </div>
      <div class="gm-metric-title">
        {{ title }}
      </div>
    </div>
    
    <div class="gm-metric-content">
      <div class="gm-metric-value">
        {{ formattedValue }}
      </div>
      
      <div v-if="description" class="gm-metric-description">
        {{ description }}
      </div>
      
      <div v-if="hasTrend" class="gm-metric-trend">
        <span class="gm-trend-icon" :style="{ color: trendColor }">
          {{ trend.value > 0 ? '↗️' : trend.value < 0 ? '↘️' : '➡️' }}
        </span>
        <span :style="{ color: trendColor }">
          {{ trend.percentage ? `${Math.abs(trend.percentage)}%` : Math.abs(trend.value) }}
        </span>
        <span class="gm-trend-period">
          {{ trend.period || 'vs last period' }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gm-metric-card {
  background: white;
  border-radius: 10px;
  padding: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #f1f5f9;
  border-left: 3px solid var(--metric-color);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.gm-metric-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-left-color: var(--metric-color);
}

.gm-metric-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.gm-metric-icon {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, var(--metric-color), var(--metric-color)dd);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.gm-metric-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  line-height: 1.3;
  flex: 1;
}

.gm-metric-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.gm-metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.gm-metric-description {
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.4;
  margin-bottom: 0.5rem;
}

.gm-metric-trend {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  margin-top: auto;
}

.gm-trend-icon {
  font-size: 0.875rem;
}

.gm-trend-period {
  color: #6b7280;
  font-weight: 400;
  font-size: 0.75rem;
}

.gm-metric-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--metric-color)06, transparent);
  border-radius: 0 12px 0 50px;
  pointer-events: none;
}

@media (max-width: 768px) {
  .gm-metric-card {
    padding: 1rem;
  }
  
  .gm-metric-value {
    font-size: 1.5rem;
  }
  
  .gm-metric-icon {
    width: 32px;
    height: 32px;
    font-size: 1rem;
  }
}
</style>
